import type {ExcludeStrict, ExtractStrict} from 'type-zoo'
import type {
  TActionCard,
  TActionCardData,
  TCard,
  TCardDataOfType,
  TValuableCardData,
  TMajorActionData,
  TMajorActionDataThatAllowsMinorAction,
  TMajorActionDirectionalRentData,
  TMajorActionDoubleTheRentData,
  TMajorActionUniversalRentData,
  TMinorActionData,
  TMinorActionDebtCollectorBirthdayType,
  TMinorActionPromptData,
  TMinorActionRentType,
  TPlay,
  TPlayerId,
  TPortfolioNeighborhoodData,
  TStandardPropertyCard,
} from '@kapitalist/contract/types'
import type {TMillions} from '@kapitalist/contract/types/millions'
import {unreachableCase} from '@kapitalist/utils/exhaustive'
import {recordKeySet} from '@kapitalist/utils/Record'
import * as S from '@kapitalist/utils/Set'
import {withOrdinalSuffix} from '~/utils/language'

// NB: order here is used in neighborhoodOrderMap - lowest to highest "rank" visually
export const allStandardPropertyTypes = recordKeySet<TStandardPropertyCard>({
  railroad: true,
  utility: true,
  brown: true,
  lightBlue: true,
  pink: true,
  orange: true,
  red: true,
  yellow: true,
  green: true,
  darkBlue: true,
})

export type TNeighborhood = TPortfolioNeighborhoodData['neighborhood']

export const neighborhoodOrderMap = (() => {
  const map = S.toMap<TNeighborhood, number>((_, i) => i, allStandardPropertyTypes)
  map.set('alone', allStandardPropertyTypes.size)
  return map
})()

export type TPropertyType = TStandardPropertyCard | 'wild'

// notably excludes 'doubleTheRent' which is a TActionCard instead
export type TRentCard = ExtractStrict<
  TActionCard,
  | 'directionalRent'
  | 'rentDarkBlueGreen'
  | 'rentLightBlueBrown'
  | 'rentPinkOrange'
  | 'rentUtilityRailroad'
  | 'rentRedYellow'
>
export type TRentCardData = TCardDataOfType<TRentCard>
export type TNonRentActionCard = ExcludeStrict<TActionCard, TRentCard>
export type TNonRentActionCardData = TCardDataOfType<TNonRentActionCard>
export type TPlayableCardData = TActionCardData // === TNonRentActionCard | TRentCardData

export const cardStarRating: Record<TCard, 1 | 2 | 3 | 4 | 5> = {
  propertyWildCard: 5,
  darkBlue: 5,
  wildDarkBlueAndGreen: 5,

  green: 4,
  wildGreenAndRailroad: 4,
  yellow: 4,
  wildRedAndYellow: 4,

  red: 3,
  orange: 3,
  wildPinkAndOrange: 3,
  utility: 3,
  brown: 3,

  wildUtilityAndRailroad: 3,
  wildLightBlueAndBrown: 3,

  wildLightBlueAndRailroad: 2,
  pink: 2,
  lightBlue: 1,

  railroad: 1,

  directionalRent: 3,
  rentDarkBlueGreen: 5,
  rentLightBlueBrown: 1,
  rentPinkOrange: 2,
  rentUtilityRailroad: 2,
  rentRedYellow: 3,

  dealBreaker: 5,
  justSayNo: 5,
  slyDeal: 4,
  forcedDeal: 4,
  debtCollector: 4,
  birthday: 3,
  doubleTheRent: 3,
  passGo: 1,

  house: 2,
  hotel: 2,

  tenMil: 2,
  fiveMil: 4,
  fourMil: 3,
  threeMil: 3,
  twoMil: 3,
  oneMil: 3,
}

export type TMajorActionType = TMajorActionData['type']
// only some major actions can have associated minor actions (pay, just say no, accept, acquiesce)
// since e.g. playing cards as cash can't have minor turn responses / be "in-progress"
export type TMajorActionTypesThatAllowMinorActions =
  TMajorActionDataThatAllowsMinorAction['type']

export type TMajorActionTypesThatDontAllowMinorActions = ExcludeStrict<
  TMajorActionType,
  TMajorActionTypesThatAllowMinorActions
>

export type TMajorActionPaySomethingData =
  | TMajorActionDirectionalRentData
  | TMajorActionUniversalRentData
  | TMajorActionDoubleTheRentData

// this is backend -> frontend data only
// this describes what happened to cause a minor action to be requested from a player
export type TMinorActionPromptType = TMinorActionPromptData['type']

// all TMinorActionPromptData share these common traits
export type TMinorActionPromptDataBase = {
  type: TMinorActionPromptType

  // "who must act?"
  minorActorPlayerId: TPlayerId

  // the rest of the data describes "what must they act upon?"
  //
  // note this is different than a `TMajorActionData` because:
  // - the server will calculate e.g. `millionsOwed` for rents / debt collectors / birthdays
  // - `minorActorPlayerId` on e.g. `TMajorActionDirectionalRentData` doesn't need to be set,
  //    that's the `minorActorPlayerId` above.
  // - there is also the case that the prompt is a JustSayNo (not a Major type); if
  //    someone JustSayNo'd you, and you must respond.
}
export type TMinorActionPromptPaySomethingBaseData = TMinorActionPromptDataBase & {
  type:
    | 'universalRent'
    | 'directionalRent'
    | 'doubleTheRent'
    | 'debtCollector'
    | 'birthday'
  millionsOwed: TMillions
}

export type TMinorActionType = TMinorActionData['type']

export type TMinorActionBaseData = {
  type: TMinorActionType
  minorActorPlayerId: TPlayerId // who took this minor action
}

export type TMinorActionPaySomethingType =
  | TMinorActionRentType
  | TMinorActionDebtCollectorBirthdayType // lovingly referred to as 'Other' elsewhere

// either paying all rent types or debtCollectors / birthdays, they all look like this
export type TMinorActionDataPaySomething = {
  type: TMinorActionPaySomethingType
  millionsOwed: TMillions
  paidWith: TValuableCardData[]
  minorActorPlayerId: TPlayerId // who took this minor action
}

export const previousPlay = (
  currentPlay: ExcludeStrict<TPlay, 'firstPlay' | 'discardPlay'>
): ExtractStrict<TPlay, 'firstPlay' | 'secondPlay'> => {
  switch (currentPlay) {
    case 'secondPlay':
      return 'firstPlay'
    case 'thirdPlay':
      return 'secondPlay'
    default:
      return unreachableCase(currentPlay)
  }
}

export const playHuman: Record<TPlay, string> = {
  firstPlay: 'First play',
  secondPlay: `Second Play`,
  thirdPlay: `Third Play`,
  discardPlay: `Discard`,
}

export const playHumanNumeric: Record<TPlay, string> = {
  firstPlay: '1/3',
  secondPlay: `2/3`,
  thirdPlay: `3/3`,
  discardPlay: `Discard`,
}

export const playHumanOrdinal: Record<
  ExcludeStrict<TPlay, 'discardPlay'>,
  string
> = {
  firstPlay: withOrdinalSuffix(1),
  secondPlay: withOrdinalSuffix(2),
  thirdPlay: withOrdinalSuffix(3),
}

export const movesLeftForPlay: Record<TPlay, 3 | 2 | 1 | 0> = {
  firstPlay: 3,
  secondPlay: 2,
  thirdPlay: 1,
  discardPlay: 0,
}
